// validateVRCPro.js — Validación estructurada del wizard VRC PRO.
//
// Devuelve la misma forma que `validateForm` en CreateProperty.jsx para que
// la UI pueda renderizar un resumen de errores y marcar cada input con borde
// rojo via `fieldErrors[name]`.
//
//   const { isValid, errors, errorsMap, warnings } = validateVRCInputs(state);
//
//   - errors:   Array<{ field, message }>  → bloqueantes (impiden el cálculo)
//   - warnings: Array<string>              → no bloqueantes (info al usuario)
//   - errorsMap: { [field]: message }      → para resaltar inputs
//
// Patrones numéricos: usamos los mismos criterios que CreateProperty
// (`/^\d+$/` para enteros, `/^\d+(\.\d{1,2})?$/` para decimales 2 dec).
// Los precios y superficies son ENTEROS no negativos.

// ── Patrones ───────────────────────────────────────────────────────────────
const RE_INT = /^\d+$/;                 // entero ≥ 0
const RE_DEC = /^\d+(\.\d{1,2})?$/;     // decimal con hasta 2 dec

// Etiquetas legibles por campo (para mensajes de error)
export const VRC_FIELD_LABELS = {
  // generales
  propietario: 'Propietario',
  agente: 'Agente',
  dir: 'Dirección',
  zona: 'Zona',
  fecha: 'Fecha',
  precioAsp: 'Precio aspirado',
  // edificado
  supCub: 'Superficie cubierta',
  supSemi: 'Superficie semicubierta',
  supTer: 'Superficie del terreno',
  antiguedad: 'Antigüedad',
  dorm: 'Dormitorios',
  ban: 'Baños',
  coch: 'Cocheras',
  piso: 'Pisos',
  frenteReal: 'Frente real',
  // terreno
  tSup: 'Superficie total del terreno',
  tFr: 'Frente del lote',
  tFo: 'Fondo del lote',
  // comparables (sufijo con índice 1/2/3)
  comp1_precio: 'Comparable 1 – Precio',
  comp2_precio: 'Comparable 2 – Precio',
  comp3_precio: 'Comparable 3 – Precio',
  comp1_supCub: 'Comparable 1 – Sup. cubierta',
  comp2_supCub: 'Comparable 2 – Sup. cubierta',
  comp3_supCub: 'Comparable 3 – Sup. cubierta',
  comp1_supTer: 'Comparable 1 – Sup. terreno',
  comp2_supTer: 'Comparable 2 – Sup. terreno',
  comp3_supTer: 'Comparable 3 – Sup. terreno',
  comp1_antiguedad: 'Comparable 1 – Antigüedad',
  comp2_antiguedad: 'Comparable 2 – Antigüedad',
  comp3_antiguedad: 'Comparable 3 – Antigüedad',
  // colectivo
  comparables: 'Comparables',
  superficies: 'Superficies',
  manualVRC: 'VRC manual',
};

export const getVRCFieldLabel = (field) => VRC_FIELD_LABELS[field] || field;

// Helpers
const isNonEmpty = (v) => v !== null && v !== undefined && String(v).trim() !== '';

/** Empuja un error si el valor existe y no matchea la regex / no es positivo. */
function validateNumber(errors, field, value, { decimal = false, required = false, positive = false } = {}) {
  const raw = isNonEmpty(value) ? String(value).trim() : '';
  if (!raw) {
    if (required) {
      errors.push({ field, message: `${getVRCFieldLabel(field)} es obligatorio.` });
    }
    return;
  }
  const re = decimal ? RE_DEC : RE_INT;
  if (!re.test(raw)) {
    errors.push({
      field,
      message: `${getVRCFieldLabel(field)} debe ser un número ${decimal ? 'válido (ej: 3 o 3.50)' : 'entero ≥ 0'}.`,
    });
    return;
  }
  const num = Number(raw);
  if (Number.isNaN(num)) {
    errors.push({ field, message: `${getVRCFieldLabel(field)} no es un número válido.` });
    return;
  }
  if (positive && num <= 0) {
    errors.push({ field, message: `${getVRCFieldLabel(field)} debe ser mayor a 0.` });
  }
}

/**
 * Valida un comparable individual. Recibe el índice (1-based) para etiquetas.
 * Un comparable se considera "vacío" cuando NO tiene precio NI superficie:
 * en ese caso no se reportan errores (es opcional).
 */
function validateComparable(errors, idx, comp) {
  if (!comp) return false;
  const precio = String(comp.precio || '').trim();
  const supCub = String(comp.supCub || '').trim();
  const supTer = String(comp.supTer || '').trim();
  const antig = String(comp.antiguedad || '').trim();

  const tieneAlgo = precio || supCub || supTer || antig;
  if (!tieneAlgo) return false; // comparable vacío, no validamos

  // Si tiene cualquier dato, exigimos precio y al menos una superficie
  validateNumber(errors, `comp${idx}_precio`, precio, { required: true, positive: true });
  if (!supCub && !supTer) {
    errors.push({
      field: `comp${idx}_supCub`,
      message: `Comparable ${idx}: indicá superficie cubierta o de terreno.`,
    });
  } else {
    if (supCub) validateNumber(errors, `comp${idx}_supCub`, supCub);
    if (supTer) validateNumber(errors, `comp${idx}_supTer`, supTer);
  }
  if (antig) validateNumber(errors, `comp${idx}_antiguedad`, antig);

  // Es válido (a fines de "tener comparables") si pasó al menos el filtro de
  // precio > 0 y alguna superficie > 0.
  return (parseFloat(precio) > 0) && ((parseFloat(supCub) > 0) || (parseFloat(supTer) > 0));
}

/**
 * Valida el estado del wizard VRC PRO.
 *
 * @param {object} state — snapshot con los mismos nombres que las useState en VRCPro:
 *   tipoInm, manualMode, manualVRC, showComp3, comp1, comp2, comp3,
 *   supCub, supSemi, supTer, tSup, tFr, tFo, precioAsp,
 *   antiguedad, dorm, ban, coch, piso, frenteReal,
 *   zona, dir, propietario.
 */
export function validateVRCInputs(state) {
  const {
    tipoInm = 'e',
    manualMode = false,
    manualVRC = '',
    showComp3 = false,
    comp1, comp2, comp3,
    supCub, supSemi, supTer,
    tSup, tFr, tFo,
    precioAsp,
    antiguedad,
    dorm, ban, coch, piso,
    frenteReal,
    zona, dir, propietario,
  } = state || {};

  const errors = [];
  const warnings = [];
  const esTerreno = tipoInm === 't';
  const manualEnabled = manualMode && isNonEmpty(manualVRC) && parseFloat(manualVRC) > 0;

  // Modo manual: el resto de validaciones es opcional, pero el valor manual
  // debe ser un número decimal válido y > 0.
  if (manualMode) {
    validateNumber(errors, 'manualVRC', manualVRC, { decimal: true, required: true, positive: true });
  }

  // ── Validación de formato de TODOS los numéricos cargados ─────────────
  // (Aunque el `type=number` del input filtra mucho, validamos por si llega
  //  copy-paste con coma, notación científica, etc.)
  if (precioAsp) validateNumber(errors, 'precioAsp', precioAsp, { positive: true });
  if (antiguedad) validateNumber(errors, 'antiguedad', antiguedad);
  if (dorm) validateNumber(errors, 'dorm', dorm);
  if (ban) validateNumber(errors, 'ban', ban);
  if (coch) validateNumber(errors, 'coch', coch);
  if (piso) validateNumber(errors, 'piso', piso);
  if (frenteReal) validateNumber(errors, 'frenteReal', frenteReal, { decimal: true });

  if (esTerreno) {
    validateNumber(errors, 'tSup', tSup, { required: !manualEnabled, positive: !manualEnabled });
    if (tFr) validateNumber(errors, 'tFr', tFr, { decimal: true });
    if (tFo) validateNumber(errors, 'tFo', tFo, { decimal: true });
  } else {
    if (supCub) validateNumber(errors, 'supCub', supCub);
    if (supSemi) validateNumber(errors, 'supSemi', supSemi);
    if (supTer) validateNumber(errors, 'supTer', supTer);

    if (!manualEnabled) {
      const tieneSup =
        parseFloat(supCub) > 0 || parseFloat(supSemi) > 0 || parseFloat(supTer) > 0;
      if (!tieneSup) {
        errors.push({
          field: 'superficies',
          message: 'Cargá al menos una superficie del inmueble (cubierta, semicubierta o terreno).',
        });
      }
    }
  }

  // ── Comparables ────────────────────────────────────────────────────────
  if (!manualEnabled) {
    const usados = [comp1, comp2, ...(showComp3 ? [comp3] : [])];
    let validos = 0;
    usados.forEach((c, i) => {
      if (validateComparable(errors, i + 1, c)) validos++;
    });

    if (validos === 0) {
      errors.push({
        field: 'comparables',
        message: 'Necesitás al menos 1 comparable con precio y superficie cargados.',
      });
    } else if (validos === 1) {
      warnings.push('Solo hay 1 comparable válido. El resultado será más confiable con 2 o 3 comparables.');
    }
  }

  // ── Advertencias informativas ─────────────────────────────────────────
  if (!zona) warnings.push('No seleccionaste zona: el rango referencial de VU no podrá compararse.');
  if (!dir) warnings.push('No cargaste dirección del inmueble.');
  if (!propietario) warnings.push('No cargaste el propietario.');

  // ── Indexar por campo ─────────────────────────────────────────────────
  const errorsMap = {};
  errors.forEach((err) => {
    if (!errorsMap[err.field]) errorsMap[err.field] = err.message;
  });

  return { isValid: errors.length === 0, errors, errorsMap, warnings };
}

export default validateVRCInputs;
